import React from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import type { TFunction } from 'i18next';

// ── Types ───────────────────────────────────────────────────────────────────

export interface ColumnDef {
  key: string;
  type: 'string' | 'number';
}

export interface FilterRule {
  column: string;
  operator: string;
  value: string;
  logic: string;
}

// ── Helpers ─────────────────────────────────────────────────────────────────

export const STRING_OPS = ['=', '!='];
export const NUMBER_OPS = ['=', '!=', '<', '>'];

export function emptyRule(): FilterRule {
  return { column: '', operator: '', value: '', logic: 'AND' };
}

export function isRuleComplete(rule: FilterRule): boolean {
  return !!rule.column && !!rule.operator && rule.value !== '';
}

// ── Component ───────────────────────────────────────────────────────────────

interface ColumnFilterBuilderProps {
  rules: FilterRule[];
  onChange: (rules: FilterRule[]) => void;
  t: TFunction;
  columns: ColumnDef[];
  columnLabel: (key: string) => string;
  className?: string;
  noteKey?: string;
}

export default function ColumnFilterBuilder({
  rules,
  onChange,
  t,
  columns,
  columnLabel,
  className,
  noteKey,
}: ColumnFilterBuilderProps) {
  const updateField = (index: number, field: string, value: string) => {
    const updated = rules.map((r, i) => {
      if (i !== index) return r;
      const next = { ...r, [field]: value };
      if (field === 'column') {
        next.operator = '=';
        next.value = '';
      }
      return next;
    });

    const last = updated[updated.length - 1];
    if (isRuleComplete(last)) {
      updated.push(emptyRule());
    }

    onChange(updated);
  };

  const removeRule = (index: number) => {
    if (rules.length <= 1) {
      onChange([emptyRule()]);
      return;
    }
    onChange(rules.filter((_: FilterRule, i: number) => i !== index));
  };

  const toggleLogic = (index: number) => {
    onChange(rules.map((r, i) =>
      i === index ? { ...r, logic: r.logic === 'AND' ? 'OR' : 'AND' } : r
    ));
  };

  const opsForColumn = (colKey: string): string[] => {
    const def = columns.find(c => c.key === colKey);
    return def?.type === 'number' ? NUMBER_OPS : STRING_OPS;
  };

  return (
    <div className={cn("p-3.5 px-4 bg-muted rounded-lg border border-border", className)}>
      {noteKey && (
        <div className="text-[0.8rem] text-ctu-text-muted mb-2">
          {t(noteKey)}
        </div>
      )}
      {rules.map((rule, idx) => {
        const ops = rule.column ? opsForColumn(rule.column) : STRING_OPS;
        const isOnlyEmpty = rules.length === 1 && !rule.column && !rule.operator && !rule.value;

        return (
          <React.Fragment key={idx}>
            {idx > 0 && (
              <div className="flex justify-start py-1 ml-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => toggleLogic(idx)}
                  className="h-6 px-2.5 text-xs font-bold tracking-wide text-primary"
                >
                  {rule.logic || 'AND'}
                </Button>
              </div>
            )}

            <div className="flex items-center gap-2">
              <Select
                value={rule.column || undefined}
                onValueChange={(val) => updateField(idx, 'column', val)}
              >
                <SelectTrigger className={cn(
                  "min-w-[140px] h-8 text-sm",
                  !rule.column && "text-muted-foreground"
                )}>
                  <SelectValue placeholder={t('filter.column')} />
                </SelectTrigger>
                <SelectContent>
                  {columns.map(c => (
                    <SelectItem key={c.key} value={c.key}>{columnLabel(c.key)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={rule.operator || (rule.column ? '=' : undefined)}
                onValueChange={(val) => updateField(idx, 'operator', val)}
                disabled={!rule.column}
              >
                <SelectTrigger className={cn(
                  "min-w-[60px] h-8 text-sm",
                  !(rule.operator || rule.column) && "text-muted-foreground"
                )}>
                  <SelectValue placeholder={t('filter.op')} />
                </SelectTrigger>
                <SelectContent>
                  {ops.map(op => (
                    <SelectItem key={op} value={op}>{op}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Input
                type="text"
                value={rule.value}
                onChange={(e) => updateField(idx, 'value', e.target.value)}
                placeholder={t('filter.value')}
                disabled={!rule.operator}
                className="flex-1 min-w-[120px] h-8 text-sm"
              />

              {!isOnlyEmpty && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => removeRule(idx)}
                  className="h-7 w-7 text-muted-foreground hover:text-destructive"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
              {isOnlyEmpty && <span className="w-7" />}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}
